import fetchWithAuth from "@/plugins/fetch";

export default class FluxBotClient {

	baseURI = "https://bot-api.fogo.fluxbeam.xyz"
	// baseURI = "http://localhost:8081"

	// Separate base URI for v1 endpoints
	v1BaseURI = "https://api.fogo.fluxbeam.xyz" // Adjust this to match the correct API domain

	// Regular fetch for external APIs without credentials
	async fetchExternal(url) {
		try {
			console.log(`Fetching external: ${url}`);
			const response = await fetch(url, {
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/json',
				},
				// Don't include credentials for external APIs
			});

			if (!response.ok) {
				const errorText = await response.text();
				console.error(`HTTP error! Status: ${response.status}, Response: ${errorText}`);
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();
			console.log(`External API response from ${url}:`, data);
			return data;
		} catch (error) {
			console.error('Error fetching external API:', error);
			throw error;
		}
	}

	async getPoints(walletAddr) {
		try {
			const response = await fetchWithAuth(`${this.baseURI}/users/${walletAddr}/points`);
			const data = await response.json();
			console.log('Points data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching points:', error);
			return null;
		}
	}

	async getReferrals(walletAddr) {
		try {
			const response = await fetchWithAuth(`${this.baseURI}/users/${walletAddr}/referrals`);
			const data = await response.json();
			console.log('Referrals data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching referrals:', error);
			return null;
		}
	}

	async getRank(walletAddr) {
		try {
			const response = await fetchWithAuth(`${this.baseURI}/users/${walletAddr}/ranking`);
			const data = await response.json();
			console.log('Rank data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching rank:', error);
			return null;
		}
	}

	async getPointsLeaderboard(page = 0) {
		try {
			console.log(`Fetching points leaderboard, page ${page}`);
			const response = await fetchWithAuth(`${this.baseURI}/leaderboards/points?page=${page}`);
			const data = await response.json();
			console.log('Points leaderboard data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching points leaderboard:', error);
			return [];
		}
	}

	async getReferralLeaderboard(page = 0) {
		try {
			const response = await fetchWithAuth(`${this.baseURI}/leaderboards/referrals?page=${page}`);
			const data = await response.json();
			console.log('Referral leaderboard data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching referral leaderboard:', error);
			return [];
		}
	}

	async getPnLLeaderboardDaily(page = 0) {
		try {
			console.log(`Fetching daily PnL leaderboard, page ${page}`);
			const data = await this.fetchExternal(`${this.baseURI}/v1/leaderboards/pnl/daily?page=${page}`);
			console.log('Daily PnL leaderboard raw data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching daily PnL leaderboard:', error);
			return [];
		}
	}

	async getPnLLeaderboardWeekly(page = 0) {
		try {
			console.log(`Fetching weekly PnL leaderboard, page ${page}`);
			const data = await this.fetchExternal(`${this.baseURI}/v1/leaderboards/pnl/weekly?page=${page}`);
			console.log('Weekly PnL leaderboard raw data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching weekly PnL leaderboard:', error);
			return [];
		}
	}

	async getPnLLeaderboardMonthly(page = 0) {
		try {
			console.log(`Fetching monthly PnL leaderboard, page ${page}`);
			const data = await this.fetchExternal(`${this.baseURI}/v1/leaderboards/pnl/monthly?page=${page}`);
			console.log('Monthly PnL leaderboard raw data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching monthly PnL leaderboard:', error);
			return [];
		}
	}

	async getFeesLeaderboardDaily(page = 0) {
		try {
			console.log(`Fetching daily fees leaderboard, page ${page}`);
			const data = await this.fetchExternal(`${this.baseURI}/v1/leaderboards/fees/daily?page=${page}`);
			console.log('Daily fees leaderboard raw data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching daily fees leaderboard:', error);
			return [];
		}
	}

	async getFeesLeaderboardWeekly(page = 0) {
		try {
			console.log(`Fetching weekly fees leaderboard, page ${page}`);
			const data = await this.fetchExternal(`${this.baseURI}/v1/leaderboards/fees/weekly?page=${page}`);
			console.log('Weekly fees leaderboard raw data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching weekly fees leaderboard:', error);
			return [];
		}
	}

	async getFeesLeaderboardMonthly(page = 0) {
		try {
			console.log(`Fetching monthly fees leaderboard, page ${page}`);
			const data = await this.fetchExternal(`${this.baseURI}/v1/leaderboards/fees/monthly?page=${page}`);
			console.log('Monthly fees leaderboard raw data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching monthly fees leaderboard:', error);
			return [];
		}
	}

	async getTopFeesTraders() {
		try {
			console.log('Fetching top fees traders');
			const data = await this.fetchExternal(`${this.baseURI}/v1/leaderboards/fees/top`);
			console.log('Top fees traders data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching top fees traders:', error);
			return [];
		}
	}

	async getTopPnLTraders() {
		try {
			console.log('Fetching top PnL traders');
			const data = await this.fetchExternal(`${this.baseURI}/v1/leaderboards/pnl/top`);
			console.log('Top PnL traders data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching top PnL traders:', error);
			return [];
		}
	}

	async getUserPnLRank(walletAddr) {
		try {
			const response = await fetchWithAuth(`${this.baseURI}/users/${walletAddr}/pnl_ranking`);
			const data = await response.json();
			console.log('User PnL rank data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching user PnL rank:', error);
			return null;
		}
	}

	async getUserFeesRank(walletAddr) {
		try {
			const response = await fetchWithAuth(`${this.baseURI}/users/${walletAddr}/fees_ranking`);
			const data = await response.json();
			console.log('User fees rank data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching user fees rank:', error);
			return null;
		}
	}

	async getStats() {
		try {
			const response = await fetchWithAuth(`${this.baseURI}/stats`);
			const data = await response.json();
			console.log('Stats data:', data);
			return data;
		} catch (error) {
			console.error('Error fetching stats:', error);
			return null;
		}
	}
}